import { findEstateRegion } from './estateRegions';

export class AzureRegionDetails {
  readonly code: string;
  readonly name: string;
  readonly coordinates: [number, number];
  /** True for the estate's regions (estateRegions.ts), the ones users may pick. */
  readonly approved: boolean;

  constructor(code: string, name: string, coordinates: [number, number]) {
    this.code = code;
    this.name = name;
    this.coordinates = coordinates;
    this.approved = findEstateRegion(code) !== undefined;
  }
}

// The first eight are the estate's regions, keyed by the estate's own tokens
// (HGS-GDL-<code>-*), not Azure's abbreviations; their names must match the
// locations in estateRegions.ts. The rest exist for the map only.
const azureRegionCatalog = [
  new AzureRegionDetails('WE', 'West Europe', [4.9041, 52.3676]),
  new AzureRegionDetails('UE', 'East US 2', [-77.436, 37.5407]),
  new AzureRegionDetails('AE', 'Australia East', [151.2093, -33.8688]),
  new AzureRegionDetails('UKS', 'UK South', [-0.1276, 51.5074]),
  new AzureRegionDetails('UC', 'Central US', [-93.625, 41.5868]),
  new AzureRegionDetails('SA', 'Southeast Asia', [103.8198, 1.3521]),
  new AzureRegionDetails('EA', 'East Asia', [114.1694, 22.3193]),
  new AzureRegionDetails('NE', 'North Europe', [-6.2603, 53.3498]),
  new AzureRegionDetails('ACL', 'Australia Central', [149.13, -35.2809]),
  new AzureRegionDetails('ACL2', 'Australia Central 2', [149.13, -35.2809]),
  new AzureRegionDetails('ASE', 'Australia Southeast', [144.9631, -37.8136]),
  new AzureRegionDetails('AUE', 'Austria East', [16.3738, 48.2082]),
  new AzureRegionDetails('BEC', 'Belgium Central', [4.3517, 50.8503]),
  new AzureRegionDetails('BRS', 'Brazil South', [-46.6333, -23.5505]),
  new AzureRegionDetails('BSE', 'Brazil Southeast', [-43.1729, -22.9068]),
  new AzureRegionDetails('CNC', 'Canada Central', [-79.3832, 43.6532]),
  new AzureRegionDetails('CNE', 'Canada East', [-71.208, 46.8139]),
  new AzureRegionDetails('INC', 'Central India', [73.8567, 18.5204]),
  new AzureRegionDetails('ISC', 'India South Central', [78.4867, 17.385]),
  new AzureRegionDetails('INS', 'South India', [80.2707, 13.0827]),
  new AzureRegionDetails('CLC', 'Chile Central', [-70.6693, -33.4489]),
  new AzureRegionDetails('DKE', 'Denmark East', [12.5683, 55.6761]),
  new AzureRegionDetails('FRC', 'France Central', [2.3522, 48.8566]),
  new AzureRegionDetails('FRS', 'France South', [5.3698, 43.2965]),
  new AzureRegionDetails('GN', 'Germany North', [13.405, 52.52]),
  new AzureRegionDetails('GWC', 'Germany West Central', [8.6821, 50.1109]),
  new AzureRegionDetails('IDC', 'Indonesia Central', [106.8456, -6.2088]),
  new AzureRegionDetails('ILC', 'Israel Central', [35.2137, 31.7683]),
  new AzureRegionDetails('ITN', 'Italy North', [9.19, 45.4642]),
  new AzureRegionDetails('JPE', 'Japan East', [139.6917, 35.6895]),
  new AzureRegionDetails('JPW', 'Japan West', [135.5023, 34.6937]),
  new AzureRegionDetails('KRC', 'Korea Central', [126.978, 37.5665]),
  new AzureRegionDetails('KRS', 'Korea South', [129.0756, 35.1796]),
  new AzureRegionDetails('MXC', 'Mexico Central', [-99.1332, 19.4326]),
  new AzureRegionDetails('NOE', 'Norway East', [10.7522, 59.9139]),
  new AzureRegionDetails('PLC', 'Poland Central', [21.0122, 52.2297]),
  new AzureRegionDetails('QAC', 'Qatar Central', [51.531, 25.2854]),
  new AzureRegionDetails('SAN', 'South Africa North', [28.0473, -26.2041]),
  new AzureRegionDetails('SAW', 'South Africa West', [18.4241, -33.9249]),
  new AzureRegionDetails('SPC', 'Spain Central', [-3.7038, 40.4168]),
  new AzureRegionDetails('SWEC', 'Sweden Central', [18.0686, 59.3293]),
  new AzureRegionDetails('SWN', 'Switzerland North', [8.5417, 47.3769]),
  new AzureRegionDetails('SWW', 'Switzerland West', [6.1432, 46.2044]),
  new AzureRegionDetails('UAC', 'UAE Central', [54.3773, 24.4539]),
  new AzureRegionDetails('UAN', 'UAE North', [55.2708, 25.2048]),
  new AzureRegionDetails('UKW', 'UK West', [-3.1791, 51.4816]),
  new AzureRegionDetails('WUS', 'West US', [-122.3321, 47.6062]),
  new AzureRegionDetails('WUS2', 'West US 2', [-122.6765, 45.5231]),
  new AzureRegionDetails('WUS3', 'West US 3', [-112.074, 33.4484]),
  new AzureRegionDetails('WCUS', 'West Central US', [-104.9903, 39.7392]),
];

export function getAzureRegions(): AzureRegionDetails[] {
  return azureRegionCatalog;
}

export function getApprovedAzureRegions(): AzureRegionDetails[] {
  return azureRegionCatalog.filter(region => region.approved);
}

export function getApprovedAzureRegionOptions(): Array<{
  label: string;
  value: string;
}> {
  return getApprovedAzureRegions().map(region => ({
    label: region.name,
    value: region.code,
  }));
}

export function getAzureRegionName(regionCode: string): string | undefined {
  return azureRegionCatalog.find(region => region.code === regionCode)?.name;
}

export function getAzureRegionCoordinates(
  regionCode: string,
): [number, number] | undefined {
  return azureRegionCatalog.find(region => region.code === regionCode)
    ?.coordinates;
}

export function getAzureRegionCoordinatesMap(): Record<
  string,
  [number, number]
> {
  return Object.fromEntries(
    azureRegionCatalog.map(region => [region.code, region.coordinates]),
  ) as Record<string, [number, number]>;
}
